import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Odontogram } from './Odontogram'
import { formatCurrency } from '@/lib/utils'
import { Id } from '../../../convex/_generated/dataModel'
import { Plus, Trash2 } from 'lucide-react'
import { usePermissions } from '@/hooks/useAuth'
import { resolveServicePricingMode } from '../../../convex/lib/applicationType'
import { getClinicalSelectionStatus } from './clinicalSelectionValidation'

/**
 * In modalità "mista" ogni componente ha SOLO:
 * - Prestazione
 * - Odontogramma (denti / impianti)
 * - Nota specifica
 *
 * Tutto il resto (tipo prova, tinta, scansione/chiama medico/presa fisica,
 * data/ora, nota di gruppo) è condiviso tra tutti i componenti e gestito
 * nel form principale.
 */
export type MixedRowState = {
  prestazione_id: string
  selectedTeeth: string[]
  selectedImplants: string[]
  implantTypes: Record<string, string>
  note: string
}

export function emptyMixedRow(): MixedRowState {
  return {
    prestazione_id: '',
    selectedTeeth: [],
    selectedImplants: [],
    implantTypes: {},
    note: '',
  }
}

function RowPrice({
  prestazione_id,
  selectedTeeth,
}: {
  prestazione_id: string
  selectedTeeth: string[]
}) {
  const { hasPermission } = usePermissions()
  const canViewPrices = hasPermission('view_prices')
  const q = useQuery(
    api.listino.calculatePrice,
    canViewPrices && prestazione_id && selectedTeeth.length > 0
      ? {
          serviceId: prestazione_id as Id<'listino'>,
          selectedTeeth,
        }
      : 'skip'
  )
  if (!canViewPrices) return null
  if (!prestazione_id || selectedTeeth.length === 0) return null
  if (q === undefined) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-600">Prezzo stimato:</span>
        <span className="text-sm text-gray-500">Calcolo prezzo…</span>
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-gray-600">Prezzo stimato:</span>
      <span className="text-sm font-medium text-gray-900">
        {formatCurrency(q.finalPrice)}
      </span>
    </div>
  )
}

type MixedPrescriptionRowsProps = {
  companyId?: Id<'companies'>
  rows: MixedRowState[]
  services: Array<{
    _id: string
    nome_visualizzato?: string
    nome_prestazione?: string
    pricing_mode?: 'per_tooth' | 'per_arch' | 'per_mouth'
  }>
  onChange: (next: MixedRowState[]) => void
  hasAttemptedSubmit: boolean
}

export function MixedPrescriptionRows({
  companyId,
  rows,
  services,
  onChange,
  hasAttemptedSubmit,
}: MixedPrescriptionRowsProps) {
  const updateRow = (i: number, patch: Partial<MixedRowState>) => {
    const next = rows.map((r, j) => (j === i ? { ...r, ...patch } : r))
    onChange(next)
  }

  const addRow = () => {
    onChange([...rows, emptyMixedRow()])
  }

  const removeRow = (i: number) => {
    if (rows.length <= 2) return
    onChange(rows.filter((_, j) => j !== i))
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>Componenti clinici (minimo 2)</CardTitle>
          <p className="mt-1 text-xs text-gray-500">
            Per ogni componente scegli solo prestazione, odontogramma e nota specifica.
            Fasi di lavoro, tinta, opzioni di consegna, data e ora valgono per tutti.
          </p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={addRow}>
          <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
          Aggiungi componente
        </Button>
      </CardHeader>
      <CardContent className="space-y-8">
        {rows.map((row, index) => {
          const selectedService = services.find(
            (service) => service._id === row.prestazione_id
          )
          const pricingMode = selectedService
            ? resolveServicePricingMode(selectedService)
            : undefined
          const selectionStatus = getClinicalSelectionStatus({
            selectedTeeth: row.selectedTeeth,
            selectedImplants: row.selectedImplants,
            implantTypes: row.implantTypes,
            pricingMode,
          })
          const serviceId = `mixed-service-${index}`
          const serviceErrorId = `${serviceId}-error`
          const rowInvalid = !row.prestazione_id || !selectionStatus.valid

          return (
          <div
            key={index}
            className={`rounded-lg border p-4 space-y-4 ${
              hasAttemptedSubmit && rowInvalid ? 'border-red-200 bg-red-50/20' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">Componente {index + 1}</h4>
              {rows.length > 2 && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-red-600"
                  onClick={() => removeRow(index)}
                  aria-label={`Rimuovi componente ${index + 1}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </div>

            <div>
              <label htmlFor={serviceId} className="mb-1 block text-sm font-medium text-gray-700">
                Prestazione *
              </label>
              <select
                id={serviceId}
                className={`w-full rounded-md border border-input bg-background px-3 py-2 text-sm ${
                  hasAttemptedSubmit && !row.prestazione_id ? 'border-red-500' : ''
                }`}
                value={row.prestazione_id}
                aria-invalid={hasAttemptedSubmit && !row.prestazione_id}
                aria-describedby={hasAttemptedSubmit && !row.prestazione_id ? serviceErrorId : undefined}
                onChange={(e) =>
                  updateRow(index, {
                    prestazione_id: e.target.value,
                  })
                }
              >
                <option value="">Seleziona…</option>
                {services.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.nome_visualizzato || s.nome_prestazione}
                  </option>
                ))}
              </select>
              {hasAttemptedSubmit && !row.prestazione_id && (
                <p id={serviceErrorId} className="mt-1 text-sm text-red-700" role="alert">
                  Manca la prestazione del componente {index + 1}. Selezionala per abilitare denti e impianti.
                </p>
              )}
            </div>

            <RowPrice
              prestazione_id={row.prestazione_id}
              selectedTeeth={row.selectedTeeth}
            />

            {!row.prestazione_id ? (
              <p className="rounded border border-yellow-200 bg-yellow-50 p-2 text-sm text-yellow-900">
                Seleziona la prestazione: dopo potrai indicare i denti obbligatori e gli eventuali impianti.
              </p>
            ) : (
                <Odontogram
                companyId={companyId}
                selectedTeeth={row.selectedTeeth}
                selectedImplants={row.selectedImplants}
                implantTypes={row.implantTypes || {}}
                onSelectionChange={(teeth, implants, types) =>
                  updateRow(index, {
                    selectedTeeth: teeth,
                    selectedImplants: implants,
                    implantTypes: types,
                  })
                }
                disabled={!row.prestazione_id}
                pricingMode={pricingMode}
                showValidationErrors={hasAttemptedSubmit}
                statusId={`mixed-component-${index}-clinical-selection-status`}
              />
            )}

            <div>
              <label htmlFor={`mixed-note-${index}`} className="mb-1 block text-sm font-medium text-gray-700">
                Note componente
              </label>
              <textarea
                id={`mixed-note-${index}`}
                className="w-full rounded-md border border-input px-3 py-2 text-sm"
                rows={2}
                value={row.note}
                onChange={(e) => updateRow(index, { note: e.target.value })}
                placeholder="Aggiungi indicazioni specifiche per questo componente"
              />
            </div>
          </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
